(function () {
  "use strict";

  /*
   * Raftor Website HQ — runtime for all web template blocks.
   *
   * Product templates (Item Grid, Item Carousel, Product Card)
   *   All three mount websitehq's own card + grid (public/js/product_card/).
   *   One batched fetch, one render pass, no post-paint DOM patching. Cart
   *   and wishlist binding still goes through webshop's own helpers — only
   *   the markup is ours.  See PRODUCT_CARD_PLAN.md.
   *
   * YouTube templates (YouTube Gallery, YouTube Carousel)
   *   Fetches via a server-side whitelisted method so the API key is never
   *   exposed to the browser.  Clicking a thumbnail opens a Bootstrap modal.
   */

  // ── Lazy Swiper loader ─────────────────────────────────────────────────
  // Swiper (151KB min) used to ship in <head> on every page. Only pages
  // that render one of these blocks actually need it, so it is injected
  // on demand from the raw (unhashed, immutable-cached) asset paths.
  //
  // Reuse in new blocks: never add Swiper back to hooks/bundle. Add your
  // block's root class to SWIPER_MARKER (so the fetch starts at frappe.ready
  // in parallel with your data load) and wrap the `new Swiper(...)` call in
  // `window.rfhqEnsureSwiper().then(...)`.
  const SWIPER_MARKER =
    ".rfhq-content-carousel, .rfhq-item-carousel, .rfhq-youtube-carousel, .rfhq-hero-slider";
  let swiperReady = null;

  function ensureSwiper() {
    if (window.Swiper) return Promise.resolve();
    if (swiperReady) return swiperReady;
    swiperReady = new Promise(function (resolve) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "/assets/raftor_websitehq/css/swiper-bundle.min.css";
      document.head.appendChild(link);

      const script = document.createElement("script");
      script.src = "/assets/raftor_websitehq/js/swiper-bundle.min.js";
      // Fail-soft: consumers re-check window.Swiper after the promise, so a
      // failed load just means carousels render as static slides.
      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });
    return swiperReady;
  }

  // Public contract, same style as window.rfhqDom / window.rfhqImageLightbox.
  window.rfhqEnsureSwiper = ensureSwiper;

  // Exposed so a caller that can't be a Web Page Block (PDP, checkout - see
  // recommendations/pdp.js, recommendations/cart.js) can still mount through
  // the exact same code every Item Grid/Item Carousel block already runs
  // through, instead of a second implementation of the same mounting logic.
  // register.js (which creates window.rfhqProductCard) runs before this file
  // in websitehq.bundle.js, so the namespace already exists here.
  window.rfhqProductCard.mountCarousel = mountProductCarousel;
  window.rfhqProductCard.mountGrid = mountProductGrid;

  frappe.ready(function () {

    // Start the Swiper fetch immediately if this page needs it, so it loads
    // in parallel with the product/YouTube AJAX calls instead of after them.
    if (document.querySelector(SWIPER_MARKER)) ensureSwiper();

    // ── Product blocks ─────────────────────────────────────────────────────
    // Rendered by websitehq's own card + grid (public/js/product_card/), not
    // by webshop.ProductGrid. One batched fetch, one render pass, no
    // post-paint DOM patching. Cart and wishlist binding still goes through
    // webshop's own helpers — only the markup is ours.
    document.querySelectorAll(".rfhq-item-grid").forEach(mountProductGrid);
    document.querySelectorAll(".rfhq-item-carousel").forEach(mountProductCarousel);
    document.querySelectorAll(".rfhq-product-card").forEach(mountProductCard);

    // ── YouTube blocks ──────────────────────────────────────────────────────
    document.querySelectorAll(".rfhq-youtube-gallery").forEach(function (el) {
      new RfhqYoutubeGallery(el);
    });
    document.querySelectorAll(".rfhq-youtube-carousel").forEach(function (el) {
      new RfhqYoutubeCarousel(el);
    });

    // ── Embed Card blocks ───────────────────────────────────────────────────
    document.querySelectorAll(".rfhq-embed-card").forEach(function (el) {
      new RfhqEmbedCard(el);
    });

    // ── Address Card blocks ─────────────────────────────────────────────────
    document.querySelectorAll(".rfhq-address-card").forEach(function (el) {
      new RfhqAddressCard(el);
    });

    // ── Legal Document blocks ───────────────────────────────────────────────
    document.querySelectorAll(".rfhq-legal-doc").forEach(function (el) {
      new RfhqLegalDoc(el);
    });

    // ── Layout Grid bridge — pull sibling component blocks into grid cells ──
    document.querySelectorAll(".rfhq-layout-grid").forEach(function (grid) {
      var gridInner = grid.querySelector(".rfhq-lg-grid");
      if (!gridInner) return;

      // Walk up to the outer <section class="section"> wrapper Frappe generates
      var section = grid.closest("section.section");
      if (!section) return;

      // Consume all consecutive non-section siblings that follow this grid section
      var sibling = section.nextElementSibling;
      while (sibling && !sibling.matches("section.section")) {
        var next = sibling.nextElementSibling;
        gridInner.appendChild(sibling);
        sibling = next;
      }
    });

    // ── Content Carousel (server-rendered slides, Swiper only) ─────────────
    document.querySelectorAll(".rfhq-content-carousel").forEach(function (el) {
      initSwiper(el, el.querySelector(".rfhq-swiper"));
    });

    // ── Hero Slider ─────────────────────────────────────────────────────────
    document.querySelectorAll(".rfhq-hero-slider").forEach(function (el) {
      new RfhqHeroSlider(el);
    });

    // ── Service Card subscribe CTA ──────────────────────────────────────────
    // Event delegation — single listener handles all subscribe buttons on the page.
    document.addEventListener("click", function (e) {
      var btn = e.target.closest(".rfhq-sc-cta-btn[data-cta-action='subscribe']");
      if (!btn) return;
      e.preventDefault();
      var plan = encodeURIComponent(btn.dataset.plan || "");
      if (!plan) return;
      var checkoutUrl = "/rfhq/checkout?plan=" + plan;
      var isGuest = !frappe.session || !frappe.session.user || frappe.session.user === "Guest";
      window.location.href = isGuest
        ? "/login?redirect-to=" + encodeURIComponent(checkoutUrl)
        : checkoutUrl;
    });
  });

  // ── Shared Swiper initialiser ─────────────────────────────────────────────
  // Used by Content Carousel (both the Web Template and the RFHQ Section
  // "Carousel" layout, which emits the same root class), Item Carousel and
  // YouTube Carousel.

  function initSwiper(el, swiperEl) {
    if (!swiperEl) return;
    ensureSwiper().then(function () {
      if (typeof Swiper === "undefined") return;
      buildSwiper(el, swiperEl);
    });
  }

  // The half of the config every RFHQ swiper agrees on. All four of these read
  // the same data-* contract, which is what lets one Jinja attribute set drive
  // every carousel producer; only slide SIZING and the transition effect
  // genuinely differ per producer, so those stay with the callers below.
  //
  // `data-delay` is currently emitted only by hero_slider — for the carousels
  // parseInt(undefined) is NaN and the caller's default applies, so honouring
  // it here costs them nothing and means a carousel could opt in later without
  // touching this function.
  function swiperCommon(el, autoplayDelay) {
    return {
      autoplay: el.dataset.autoplay === "true"
        ? { delay: parseInt(el.dataset.delay, 10) || autoplayDelay, disableOnInteraction: false }
        : false,
      loop: el.dataset.loop === "true",
      navigation: el.dataset.nav === "true"
        ? { nextEl: el.querySelector(".rfhq-swiper-next"),
            prevEl: el.querySelector(".rfhq-swiper-prev") }
        : false,
      pagination: el.dataset.pagination === "true"
        ? { el: el.querySelector(".rfhq-swiper-pagination"), clickable: true }
        : false,
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
        paginationBulletMessage: "Go to slide {{index}}",
      },
    };
  }

  function buildSwiper(el, swiperEl) {
    new Swiper(swiperEl, Object.assign(swiperCommon(el, 3500), {
      slidesPerView: parseInt(el.dataset.slidesMobile, 10) || 1,
      spaceBetween: 16,
      breakpoints: {
        576: { slidesPerView: parseInt(el.dataset.slidesTablet,  10) || 2, spaceBetween: 20 },
        992: { slidesPerView: parseInt(el.dataset.slidesDesktop, 10) || 4, spaceBetween: 24 },
      },
    }));
  }

  // ============================================================
  // PRODUCT TEMPLATES
  // ============================================================
  //
  // Thin wiring only. Rendering, fetching, pagination and cart state live in
  // public/js/product_card/ (window.rfhqProductCard, registered by
  // websitehq.bundle.js before this file). This reads each block's data-*
  // config and mounts a grid.
  //
  // Replaces delegation to webshop.ProductGrid, whose card was then patched
  // after paint by several raftor_webhq scripts. See PRODUCT_CARD_PLAN.md.

  function cardApi() {
    return window.rfhqProductCard || null;
  }

  // "automatic" is the absence of an override — the adapter's per-item
  // decision stands.
  function blockActionKind(el) {
    const kind = el.dataset.actionKind;
    return !kind || kind === "automatic" ? undefined : kind;
  }

  // WebshopSource's endpoint (get_product_filter_data) is a generic "list
  // published items matching these filters" query - it has no concept of
  // "similar to this item" / "based on this cart" / "for this customer" and
  // cannot serve a recommendation query no matter what filters are passed.
  // "Recommendation" is therefore the one source_type that needs a different
  // Source class, not just different filters on the same one.
  function makeSource(api, el) {
    const source = (el && el.dataset.source) || "Newest";
    if (source === "Recommendation") {
      return new api.adapters.recommendations.Source({ cartStore: api.cartStore });
    }
    if (source === "App Listing") {
      // The app's method comes from the section (Records Source); values in the page address are passed on to it.
      let args = {};
      try { args = JSON.parse(el.dataset.args || "{}") || {}; } catch (e) { args = {}; }  // fixed by the section (for example which half of a list it shows)
      const query = new URLSearchParams(window.location.search);
      (el.dataset.urlArgs || "").split(",").map((n) => n.trim()).filter(Boolean).forEach((n) => { if (query.has(n)) args[n] = query.get(n); });
      return new api.adapters.appListing.Source({ cartStore: api.cartStore, method: el.dataset.recordsSource, args });
    }
    return new api.adapters.webshop.Source({ cartStore: api.cartStore });
  }

  function buildFilters(el) {
    const source = el.dataset.source || "Newest";
    const filter = el.dataset.filter || "";
    if (source === "App Listing") return {};
    if (source === "Recommendation") {
      // Item Grid/Item Carousel blocks on an ordinary Web Page (home) have no
      // item/cart context to key off, so "for_you" (no data-recommendation-mode
      // set) is the default. PDP/checkout's JS-built carousel elements set
      // data-recommendation-mode/data-item-code explicitly - same data-*
      // contract, just populated by JS instead of Jinja since those two pages
      // aren't Web Page documents and can't carry a Web Page Block.
      return { mode: el.dataset.recommendationMode || "for_you", item_code: el.dataset.itemCode };
    }
    const fieldFilters = {};
    if (source === "Item Group" && filter) fieldFilters.item_group = [filter];
    if (source === "Brand" && filter) fieldFilters.brand = [filter];
    if (source === "Badge" && filter) fieldFilters.badges = [filter];
    return { field_filters: fieldFilters };
  }

  // Standard/Featured already show swatches/sizes as a static preview today;
  // this just upgrades that same surface to real selection, mirroring
  // /all-products. Compact/Minimal deliberately drop swatches/sizes for
  // space (their own layout files say so) and have no CSS accounting for
  // the swatch/price/button markup raftor_webhq's swatch engine injects, so
  // they keep claiming ownership and stay display-only.
  //
  // Also requires the block's action kind to be "automatic" (unset). A
  // WhatsApp/Chat/Read More block is deliberately enquiry-only — its whole
  // point is to replace the purchase path with a single enquiry action.
  // webhq's swatch engine injects its own competing Add to Cart button with
  // no awareness of that override, so letting it draw on those blocks would
  // put two conflicting primary actions on the same card.
  // Recommendation-sourced cards never delegate to webhq's variant-swatch
  // injector (product_variant_swatches.js), regardless of layout/action kind:
  // that script only fills a card's CTA slot when its own lookup finds real
  // variant attributes for the item, and silently leaves the slot empty
  // otherwise - there's no fallback plain "Add to Cart" for a simple product
  // under delegated (claimOwnership:false) rendering. raftor_webhq.catalog.
  // recommendations.* now returns an accurate has_variants per item, so
  // owned rendering (this file's own renderCard()) can correctly choose
  // Explore Variants vs Add to Cart vs Sold Out itself, for every
  // recommended item, with no dependency on that separate lookup.
  function wantsInteractiveVariants(el) {
    if (el.dataset.source === "Recommendation") return false;
    const layout = (el.dataset.cardLayout || "standard").toLowerCase();
    if (layout !== "standard" && layout !== "featured") return false;
    return blockActionKind(el) === undefined;
  }


  // A listing an app feeds may be narrowed by buttons (its feed says which). Narrowing it to nothing must not remove the section, or the buttons go
  // with it: the section keeps its buttons and says there is nothing, and comes back when a choice brings something.
  function listingEmpty(el, empty) {
    el.classList.toggle("rfhq-listing--empty", empty);
    let note = el.querySelector(":scope > .rfhq-records-empty");
    if (empty && el.dataset.empty && !note) {
      note = document.createElement("p");
      note.className = "rfhq-records-empty";
      note.setAttribute("role", "status");
      note.textContent = el.dataset.empty;
      el.appendChild(note);
    } else if (!empty && note) {
      note.remove();
    }
  }

  // The buttons of one listing narrow every other listing on the page that takes the same choices (an "online now" row and a "book for later" row).
  function followFilters(el, grid) {
    window.addEventListener("rfhq:app-filters", function (event) {
      const detail = event.detail || {};
      if (!el.isConnected || detail.origin && el.contains(detail.origin)) return;
      const mine = (el.dataset.urlArgs || "").split(",").map(function (n) { return n.trim(); }).filter(Boolean);
      if (!mine.some(function (n) { return (detail.facets || []).indexOf(n) >= 0; })) return;
      const source = grid.source;
      source.args = Object.assign({}, source.args);
      mine.forEach(function (n) { if (detail.chosen[n]) source.args[n] = detail.chosen[n]; else delete source.args[n]; });
      grid.reset();
    });
  }

  // After every fresh fetch (a choice in the buttons, here or in another listing) the section shows its cards or says it has none, and a row starts from its first card.
  function watchListing(el, grid) {
    const reset = grid.reset.bind(grid);
    grid.reset = function (filters) {
      return reset(filters).then(function () { listingEmpty(el, !grid._modelsById.size); slideBack(el); });
    };
    listingEmpty(el, !grid._modelsById.size);
    followFilters(el, grid);
  }

  function slideBack(el) {
    const swiperEl = el.querySelector(".rfhq-swiper");
    if (swiperEl && swiperEl.swiper) { swiperEl.swiper.update(); swiperEl.swiper.slideTo(0, 0); }
  }

  function isAppListing(el) { return el.dataset.source === "App Listing"; }

  function mountProductGrid(el) {
    const api = cardApi();
    const wrapper = el.querySelector(".rfhq-grid-wrapper");
    if (!api || !wrapper) return;

    const infinite = el.dataset.infiniteScroll === "true";
    el.__rfhqGrid = new api.RfhqProductGrid({
      el: wrapper,
      source: makeSource(api, el),
      filters: buildFilters(el),
      columns: parseInt(el.dataset.columns, 10) || 3,
      layout: el.dataset.cardLayout || undefined,
      actionKind: blockActionKind(el),
      showPrice: el.dataset.showPrice !== "false",
      claimOwnership: !wantsInteractiveVariants(el),
      infiniteScroll: infinite,
      // Mutually exclusive by construction: a hard cap means the block is a
      // fixed strip, not a listing.
      maxItems: infinite ? null : (parseInt(el.dataset.limit, 10) || 12),
      // A block with genuinely nothing to show (a filtered Item Group with no
      // items, a Recommendation source with its placement setting switched
      // off) disappears cleanly instead of leaving an empty title + no cards.
      onEmpty: function () {
        if (isAppListing(el)) listingEmpty(el, true);
        else el.remove();
      },
    });
    el.__rfhqGrid.load().then(function () {
      // A listing an app feeds may offer filters (its feed says which); they are drawn as a bar above the grid.
      if (isAppListing(el)) {
        if (api.mountAppFilters) api.mountAppFilters(el, el.__rfhqGrid);
        watchListing(el, el.__rfhqGrid);
      }
    });
  }

  function mountProductCarousel(el) {
    const api = cardApi();
    const wrapper = el.querySelector(".rfhq-carousel-wrapper");
    if (!api || !wrapper) return;

    // The grid stamps data-rfhq-owned on `wrapper` (its own `el`) via
    // _applySiteThemeFallback(), but this carousel's own title
    // (.rfhq-section-header .section-title, e.g. PDP's "You may also like")
    // is a SIBLING of wrapper, not a descendant - rfhq_templates.css's
    // [data-rfhq-theme] .rfhq-section-header rule structurally can never
    // match a theme stamped one level too low. Stamp the outer carousel
    // element instead, before the grid constructs: the grid's own fallback
    // check (el.closest('[data-rfhq-theme]')) will then correctly find this
    // ancestor and skip re-stamping wrapper, so cards inherit the same
    // value via ordinary CSS custom property cascade either way.
    if (window.rfhqApplyOwnedThemeFallback) {
      el.setAttribute("data-rfhq-owned", "1");
      window.rfhqApplyOwnedThemeFallback(el);
    }

    el.__rfhqGrid = new api.RfhqProductGrid({
      el: wrapper,
      source: makeSource(api, el),
      filters: buildFilters(el),
      // Cards sit in slides, so no bootstrap column — but they keep
      // .item-card, which the swatch and badge scripts key on.
      columnClass: "",
      layout: el.dataset.cardLayout || undefined,
      actionKind: blockActionKind(el),
      showPrice: el.dataset.showPrice !== "false",
      claimOwnership: !wantsInteractiveVariants(el),
      maxItems: parseInt(el.dataset.limit, 10) || 16,
      infiniteScroll: false,
      // See mountProductGrid's identical onEmpty - same reasoning, same fix.
      onEmpty: function () {
        if (isAppListing(el)) listingEmpty(el, true);
        else el.remove();
      },
      wrapCard: function (card) {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        slide.appendChild(card);
        return slide;
      },
    });
    // Swiper measures slides on construction, so initialise only once the
    // first page exists. onEmpty (above) may have already removed el from
    // the document by the time this resolves - isConnected guards against
    // initialising Swiper on a detached node.
    el.__rfhqGrid.load().then(function () {
      if (!el.isConnected) return;
      if (isAppListing(el)) {
        if (api.mountAppFilters) api.mountAppFilters(el, el.__rfhqGrid);
        watchListing(el, el.__rfhqGrid);
      }
      initSwiper(el, el.querySelector(".rfhq-swiper"));
    });
  }

  // Single-item block (RFHQ Product Card web template): one explicit item,
  // full width, never paginated.
  //
  // data-item-code is resolved server-side from the block's Website Item link
  // (website_hq/product_card.py) — the Link's own value is WEB-ITM-#####, which
  // would match nothing as an item_code filter.
  function mountProductCard(el) {
    const api = cardApi();
    const wrapper = el.querySelector(".rfhq-product-card-wrapper");
    const itemCode = el.dataset.itemCode;
    if (!api || !wrapper || !itemCode) return;

    el.__rfhqGrid = new api.RfhqProductGrid({
      el: wrapper,
      source: makeSource(api),
      filters: { field_filters: { item_code: [itemCode] } },
      layout: el.dataset.cardLayout || undefined,
      actionKind: blockActionKind(el),
      showPrice: el.dataset.showPrice !== "false",
      claimOwnership: !wantsInteractiveVariants(el),
      maxItems: 1,
      infiniteScroll: false,
      // A hand-picked item can legitimately resolve to nothing — it may be
      // unpublished, or outside the brands this customer group may see
      // (raftor_webhq applies brand restriction server-side). Hide rather than
      // leave an empty row on the page.
      onEmpty: function () {
        el.hidden = true;
      },
    });
    el.__rfhqGrid.load();
  }

  // ============================================================
  // YOUTUBE TEMPLATES
  // ============================================================

  const YT_COL = {
    "2": "col-12 col-sm-6",
    "3": "col-12 col-sm-6 col-lg-4",
    "4": "col-12 col-sm-6 col-lg-3",
  };

  function fetchVideos(el, callback) {
    const limit = parseInt(el.dataset.limit, 10) || 12;
    frappe.call({
      method: "raftor_websitehq.utils.youtube.get_youtube_videos",
      args: {
        source_type: el.dataset.source   || "Playlist",
        source_id:   el.dataset.sourceId || "",
        max_items:   limit,
      },
      callback(r) {
        if (r.message && r.message.length) callback(r.message);
      },
    });
  }

  function videoCardHtml(video) {
    const title = frappe.utils.escape_html(video.title || "");
    const thumb = video.thumbnail
      || `https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`;
    return (
      `<div class="rfhq-video-card" data-video-id="${video.video_id}">` +
        `<div class="rfhq-video-thumb-wrap">` +
          `<img src="${thumb}" alt="${title}" loading="lazy" decoding="async" class="rfhq-video-thumb">` +
          `<div class="rfhq-play-btn" aria-hidden="true">` +
            `<svg viewBox="0 0 68 48" width="54" height="38">` +
              `<path d="M66.5 7.7c-.8-2.9-3-5.2-5.9-6C55.8.8 34 .8 34 .8S12.2.8 7.4 1.7C4.6 2.5 2.3 4.8 1.5 7.7.7 12.5.7 24 .7 24s0 11.5.8 16.3c.8 2.9 3 5.2 5.9 6C12.2 47.2 34 47.2 34 47.2s21.8 0 26.6-.9c2.9-.8 5.1-3.1 5.9-6 .8-4.8.8-16.3.8-16.3s0-11.5-.8-16.3z" fill="#f00"/>` +
              `<path d="M45 24 27 14v20" fill="#fff"/>` +
            `</svg>` +
          `</div>` +
        `</div>` +
        `<p class="rfhq-video-title mt-2 mb-0">${title}</p>` +
      `</div>`
    );
  }

  // Single shared Bootstrap modal — created once, reused for every click.
  function ensureModal() {
    if (document.getElementById("rfhq-video-modal")) return;
    document.body.insertAdjacentHTML("beforeend",
      `<div id="rfhq-video-modal" class="modal fade" tabindex="-1" role="dialog">` +
        `<div class="modal-dialog modal-lg modal-dialog-centered" role="document">` +
          `<div class="modal-content">` +
            `<div class="modal-header border-0 pb-0">` +
              `<button type="button" class="close" data-dismiss="modal" aria-label="Close">` +
                `<span aria-hidden="true">&times;</span>` +
              `</button>` +
            `</div>` +
            `<div class="modal-body pt-1">` +
              `<div class="embed-responsive embed-responsive-16by9">` +
                `<iframe id="rfhq-video-frame" class="embed-responsive-item"` +
                  ` src="" allow="autoplay; encrypted-media" allowfullscreen></iframe>` +
              `</div>` +
            `</div>` +
          `</div>` +
        `</div>` +
      `</div>`
    );
    // Clear src on close so the video stops playing
    $("#rfhq-video-modal").on("hidden.bs.modal", function () {
      document.getElementById("rfhq-video-frame").src = "";
    });
  }

  function openVideoModal(src) {
    ensureModal();
    document.getElementById("rfhq-video-frame").src = src;
    $("#rfhq-video-modal").modal("show");
  }

  function bindVideoClicks(el) {
    el.addEventListener("click", function (e) {
      const card = e.target.closest(".rfhq-video-card");
      if (card && card.dataset.videoId) {
        openVideoModal("https://www.youtube.com/embed/" + card.dataset.videoId + "?autoplay=1");
      }
    });
  }

  class RfhqYoutubeGallery {
    constructor(el) {
      this.el      = el;
      this.wrapper = el.querySelector(".rfhq-youtube-wrapper");
      fetchVideos(el, this._render.bind(this));
    }

    _render(videos) {
      const col = YT_COL[this.el.dataset.columns] || YT_COL["3"];
      let html = '<div class="row">';
      videos.forEach(function (v) {
        html += `<div class="${col} mb-4">${videoCardHtml(v)}</div>`;
      });
      this.wrapper.innerHTML = html + "</div>";
      bindVideoClicks(this.el);
    }
  }

  class RfhqYoutubeCarousel {
    constructor(el) {
      this.el       = el;
      this.swiperEl = el.querySelector(".rfhq-swiper");
      this.wrapper  = el.querySelector(".rfhq-carousel-wrapper");
      fetchVideos(el, this._render.bind(this));
    }

    _render(videos) {
      const frag = document.createDocumentFragment();
      videos.forEach(function (v) {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        slide.innerHTML = videoCardHtml(v);
        frag.appendChild(slide);
      });
      this.wrapper.appendChild(frag);
      initSwiper(this.el, this.swiperEl);
      bindVideoClicks(this.el);
    }
  }
  // ============================================================
  // EMBED CARD
  // ============================================================

  // ── URL parsers ───────────────────────────────────────────────────────────

  function parseYoutubeId(url) {
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("?")[0];
      return u.searchParams.get("v");
    } catch (_) { return null; }
  }

  function parseVimeoId(url) {
    const m = url.match(/vimeo\.com\/(\d+)/);
    return m ? m[1] : null;
  }

  function makeIframe(src) {
    const f = document.createElement("iframe");
    f.src = src;
    f.setAttribute("allow", "autoplay; encrypted-media; fullscreen; picture-in-picture");
    f.setAttribute("allowfullscreen", "");
    f.setAttribute("loading", "lazy");
    f.style.cssText = "border:0;width:100%;height:100%;display:block;";
    return f;
  }

  // ── Video poster + lazy load ──────────────────────────────────────────────

  function renderVideoPoster(card, posterSrc, onPlay) {
    const poster = document.createElement("div");
    poster.className = "rfhq-video-poster";
    if (posterSrc) poster.style.backgroundImage = 'url("' + posterSrc + '")';

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "rfhq-video-play-btn";
    btn.setAttribute("aria-label", "Play video");
    btn.innerHTML =
      '<svg viewBox="0 0 68 48" width="68" height="48">' +
        '<path d="M66.5 7.7c-.8-2.9-3-5.2-5.9-6C55.8.8 34 .8 34 .8S12.2.8 7.4 1.7C4.6 2.5 2.3 4.8 1.5 7.7.7 12.5.7 24 .7 24s0 11.5.8 16.3c.8 2.9 3 5.2 5.9 6C12.2 47.2 34 47.2 34 47.2s21.8 0 26.6-.9c2.9-.8 5.1-3.1 5.9-6 .8-4.8.8-16.3.8-16.3s0-11.5-.8-16.3z" fill="#f00"/>' +
        '<path d="M45 24 27 14v20" fill="#fff"/>' +
      '</svg>';

    poster.appendChild(btn);
    poster.addEventListener("click", onPlay, { once: true });
    card.inner.innerHTML = "";
    card.inner.appendChild(poster);
  }

  // ── Custom audio player ───────────────────────────────────────────────────

  function formatTime(s) {
    if (!isFinite(s) || s < 0) return "0:00";
    return Math.floor(s / 60) + ":" + String(Math.floor(s % 60)).padStart(2, "0");
  }

  function renderAudioPlayer(card, url, autoplay) {
    const audio = document.createElement("audio");
    audio.src = url;
    audio.preload = "metadata";
    if (autoplay) audio.autoplay = true;

    const PLAY  = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>';
    const PAUSE = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
    const VOL   = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';
    const MUTE  = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';

    const player = document.createElement("div");
    player.className = "rfhq-audio-player";
    player.innerHTML =
      '<button class="rfhq-audio-play" type="button" aria-label="Play">' + PLAY + '</button>' +
      '<div class="rfhq-audio-progress">' +
        '<div class="rfhq-audio-bar"><div class="rfhq-audio-fill"></div></div>' +
        '<span class="rfhq-audio-time">0:00 / 0:00</span>' +
      '</div>' +
      '<button class="rfhq-audio-mute" type="button" aria-label="Mute">' + VOL + '</button>';

    const playBtn = player.querySelector(".rfhq-audio-play");
    const fill    = player.querySelector(".rfhq-audio-fill");
    const timeEl  = player.querySelector(".rfhq-audio-time");
    const muteBtn = player.querySelector(".rfhq-audio-mute");
    const bar     = player.querySelector(".rfhq-audio-bar");

    playBtn.addEventListener("click", function () {
      if (audio.paused) {
        audio.play();
        playBtn.innerHTML = PAUSE;
        playBtn.setAttribute("aria-label", "Pause");
      } else {
        audio.pause();
        playBtn.innerHTML = PLAY;
        playBtn.setAttribute("aria-label", "Play");
      }
    });

    audio.addEventListener("timeupdate", function () {
      const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
      fill.style.width = pct + "%";
      timeEl.textContent = formatTime(audio.currentTime) + " / " + formatTime(audio.duration);
    });

    audio.addEventListener("ended", function () {
      playBtn.innerHTML = PLAY;
      playBtn.setAttribute("aria-label", "Play");
    });

    bar.addEventListener("click", function (e) {
      if (!audio.duration) return;
      const rect = bar.getBoundingClientRect();
      audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
    });

    muteBtn.addEventListener("click", function () {
      audio.muted = !audio.muted;
      muteBtn.innerHTML = audio.muted ? MUTE : VOL;
      muteBtn.setAttribute("aria-label", audio.muted ? "Unmute" : "Mute");
    });

    player.appendChild(audio);
    if (card.wrap) {
      card.wrap.style.paddingBottom = "0";
      card.wrap.style.height = "auto";
      card.wrap.style.background = "none";
    }
    card.inner.innerHTML = "";
    card.inner.appendChild(player);
  }

  // ── Embed renderers ───────────────────────────────────────────────────────

  const EMBED_RENDERERS = {
    video(card) {
      const ytId    = parseYoutubeId(card.url);
      const vId     = !ytId && parseVimeoId(card.url);
      const isLight = card.player === "lightbox";

      if (ytId || vId) {
        const base    = ytId
          ? "https://www.youtube.com/embed/" + ytId + "?rel=0"
          : "https://player.vimeo.com/video/" + vId;
        const playSrc = base + (ytId ? "&autoplay=1" : "?autoplay=1");
        const poster  = ytId
          ? "https://img.youtube.com/vi/" + ytId + "/maxresdefault.jpg"
          : null;

        renderVideoPoster(card, poster, function () {
          if (isLight) {
            openVideoModal(playSrc);
          } else {
            card.inner.innerHTML = "";
            card.inner.appendChild(makeIframe(playSrc));
          }
        });
      } else {
        const v = document.createElement("video");
        v.src = card.url;
        v.controls = true;
        v.playsInline = true;
        v.style.cssText = "width:100%;height:100%;display:block;";
        if (card.autoplay) { v.autoplay = true; v.muted = true; }
        card.inner.innerHTML = "";
        card.inner.appendChild(v);
      }
    },

    audio(card) {
      if (card.url.includes("open.spotify.com")) {
        const f = makeIframe(card.url.replace("open.spotify.com/", "open.spotify.com/embed/"));
        f.style.borderRadius = "12px";
        if (card.wrap) { card.wrap.style.paddingBottom = "0"; card.wrap.style.height = "152px"; }
        card.inner.innerHTML = "";
        card.inner.appendChild(f);
      } else if (card.url.includes("soundcloud.com")) {
        if (card.wrap) { card.wrap.style.paddingBottom = "0"; card.wrap.style.height = "152px"; }
        card.inner.innerHTML = "";
        card.inner.appendChild(makeIframe(
          "https://w.soundcloud.com/player/?url=" + encodeURIComponent(card.url) +
          "&auto_play=" + card.autoplay + "&show_artwork=true"
        ));
      } else {
        renderAudioPlayer(card, card.url, card.autoplay);
      }
    },

    image(card) {
      const img = card.inner.querySelector("img");
      if (!img) return;
      const caption = (card.el.querySelector(".rfhq-embed-caption") || {}).textContent || "";
      // Site-wide zoom engine (image_zoom_loader.js) picks this up — works
      // regardless of the product-image hover/click mode toggle.
      img.setAttribute("data-zoom-enabled", "true");
      if (caption) img.dataset.zoomCaption = caption;
    },

    map(card) {
      let src = card.url;
      if (src.includes("google.com/maps") && !src.includes("/embed") && !src.includes("output=embed")) {
        src = "https://maps.google.com/maps?output=embed&q=" + encodeURIComponent(src);
      }
      card.inner.innerHTML = "";
      card.inner.appendChild(makeIframe(src));
    },
  };

  class RfhqEmbedCard {
    constructor(el) {
      this.el       = el;
      this.type     = el.dataset.type;
      this.url      = el.dataset.url;
      this.autoplay = el.dataset.autoplay === "true";
      this.player   = el.dataset.player || "inline";
      this.wrap     = el.querySelector(".rfhq-embed-wrap");
      this.inner    = el.querySelector(".rfhq-embed-inner");
      if (!this.url || !this.inner) return;
      const renderer = EMBED_RENDERERS[this.type];
      if (renderer) renderer(this);
    }
  }

  // ============================================================
  // HERO SLIDER
  // ============================================================

  class RfhqHeroSlider {
    constructor(el) {
      const swiperEl = el.querySelector(".rfhq-hs-swiper");
      if (!swiperEl) return;
      ensureSwiper().then(function () {
        if (typeof Swiper === "undefined") return;
        RfhqHeroSlider._build(el, swiperEl);
      });
    }

    // Shares swiperCommon() with the carousels — a hero is one full-bleed
    // slide with an optional crossfade, so slide sizing is the only thing it
    // states differently (and it never takes breakpoints). Slide height is
    // fixed (the Slider Height setting, see rfhq_templates.css) — the banner
    // is shown in full inside it via object-fit:contain, not by resizing the
    // box to the image.
    static _build(el, swiperEl) {
      new Swiper(swiperEl, Object.assign(swiperCommon(el, 4000), {
        effect: el.dataset.transition === "fade" ? "fade" : "slide",
        fadeEffect: { crossFade: true },
        slidesPerView: 1,
      }));
    }
  }

  // ============================================================
  // ADDRESS CARD
  // ============================================================

  class RfhqAddressCard {
    constructor(el) {
      el.querySelectorAll(".rfhq-copy-btn").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          const text = btn.dataset.copy || "";
          if (!text || !navigator.clipboard) return;
          navigator.clipboard.writeText(text).then(function () {
            btn.classList.add("rfhq-copy-btn--done");
            btn.setAttribute("aria-label", "Copied!");
            setTimeout(function () {
              btn.classList.remove("rfhq-copy-btn--done");
              btn.setAttribute("aria-label", "Copy");
            }, 2000);
          });
        });
      });
    }
  }

  // ============================================================
  // LEGAL DOCUMENT
  // ============================================================
  // Builds the table of contents from the <h2> headings the agent/editor
  // authored inside .rfhq-ld-body, since Jinja can't safely walk HTML
  // already stored as a rich-text blob.

  function slugify(text, seen) {
    let slug = (text || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-") || "section";
    let unique = slug;
    let i = 2;
    while (seen.has(unique)) { unique = slug + "-" + i++; }
    seen.add(unique);
    return unique;
  }

  // The TOC numbers its entries itself (see rfhq_templates.css's
  // rfhq-ld-toc-list counter), but clause headings are commonly authored
  // with their own leading number too ("1. Shipping Coverage") — strip that
  // so the two numbering systems don't stack into "1. 1. Shipping Coverage".
  function stripLeadingNumber(text) {
    return (text || "").replace(/^\s*\d+(?:\.\d+)*[.)]\s*/, "");
  }

  class RfhqLegalDoc {
    constructor(el) {
      const toc  = el.querySelector(".rfhq-ld-toc");
      const body = el.querySelector(".rfhq-ld-body");
      if (!toc || !body) return;

      const headings = body.querySelectorAll("h2");
      if (!headings.length) return;

      const seen = new Set();
      const items = [];
      headings.forEach(function (h) {
        if (!h.id) h.id = slugify(h.textContent, seen);
        else seen.add(h.id);
        const label = stripLeadingNumber(h.textContent);
        items.push('<li><a href="#' + h.id + '">' + label + "</a></li>");
      });

      toc.innerHTML =
        '<p class="rfhq-ld-toc-label">On this page</p>' +
        '<ol class="rfhq-ld-toc-list">' + items.join("") + "</ol>";
    }
  }

})();
